import { SSE_RECONNECT_BASE_MS, SSE_RECONNECT_MAX_MS } from '../../constants/timing';

export interface SseEvent {
  id?: string;
  event: string;
  data: string;
}

export interface SseSubscription {
  events(): AsyncIterableIterator<SseEvent>;
  close(): void;
}

export interface FetchSseOptions {
  getBearer: () => Promise<string>;
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

async function* parseSseStream(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
): AsyncGenerator<SseEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let eventName = 'message';
  let eventId: string | undefined;
  let dataLines: string[] = [];

  const dispatch = (): SseEvent | null => {
    if (dataLines.length === 0) {
      eventName = 'message';
      eventId = undefined;
      return null;
    }
    const event: SseEvent = {
      event: eventName,
      data: dataLines.join('\n'),
    };
    if (eventId !== undefined) {
      event.id = eventId;
    }
    eventName = 'message';
    eventId = undefined;
    dataLines = [];
    return event;
  };

  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        break;
      }
      buffer += decoder.decode(value, { stream: true });

      let newline = buffer.indexOf('\n');
      while (newline !== -1) {
        let line = buffer.slice(0, newline);
        buffer = buffer.slice(newline + 1);
        if (line.endsWith('\r')) {
          line = line.slice(0, -1);
        }

        if (line === '') {
          const event = dispatch();
          if (event) {
            yield event;
          }
        } else if (line.startsWith(':')) {
          /* comment */
        } else {
          const colon = line.indexOf(':');
          const field = colon === -1 ? line : line.slice(0, colon);
          let rawValue = colon === -1 ? '' : line.slice(colon + 1);
          if (rawValue.startsWith(' ')) {
            rawValue = rawValue.slice(1);
          }
          if (field === 'event') {
            eventName = rawValue || 'message';
          } else if (field === 'data') {
            dataLines.push(rawValue);
          } else if (field === 'id') {
            if (!rawValue.includes('\0')) {
              eventId = rawValue;
            }
          }
        }

        newline = buffer.indexOf('\n');
      }
    }

    const trailing = dispatch();
    if (trailing) {
      yield trailing;
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * One shared fetch-SSE parser/reconnect client. Bearer rides in the
 * Authorization header — never as a query-param token. On drop, resubscribe
 * without Last-Event-ID; state recovery is REST-driven (at-most-once).
 */
export function createFetchSseClient(path: string, opts: FetchSseOptions): SseSubscription {
  const abort = new AbortController();
  let started: AsyncGenerator<SseEvent> | null = null;

  async function* generate(): AsyncGenerator<SseEvent> {
    let retryDelay = SSE_RECONNECT_BASE_MS;

    while (!abort.signal.aborted) {
      try {
        const token = await opts.getBearer();
        const headers: Record<string, string> = {
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(path, {
          headers,
          signal: abort.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`SSE connection failed: ${response.status}`);
        }

        retryDelay = SSE_RECONNECT_BASE_MS;

        for await (const event of parseSseStream(response.body, abort.signal)) {
          yield event;
        }
      } catch (error) {
        if (abort.signal.aborted || isAbortError(error)) {
          return;
        }
      }

      if (abort.signal.aborted) {
        return;
      }

      try {
        await delay(retryDelay, abort.signal);
      } catch (error) {
        if (abort.signal.aborted || isAbortError(error)) {
          return;
        }
        throw error;
      }
      retryDelay = Math.min(retryDelay * 2, SSE_RECONNECT_MAX_MS);
    }
  }

  return {
    events(): AsyncIterableIterator<SseEvent> {
      if (!started) {
        started = generate();
      }
      return started;
    },
    close(): void {
      abort.abort();
    },
  };
}
